/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { IDropDownOption } from "@atg/icf-react-components/components/DropDownMenu/DropDownMenuOption";

import { PayProgramType, RequestStatus } from "~/constants";
import {
  BlackLabelSiteReviewStatusName,
  JoinType,
  PostbackEventType,
  PostbackMethodType,
  PostbackSiteType,
  SiteReviewStatus,
  SubdomainEnding,
  WizardAction,
} from "~/features/mySites/constants";
import {
  PaginatedRequest,
  PaginatedResponse,
  PaginationSettings,
  WebservicePaginatedResponse,
  WebserviceResponse,
} from "~/types";

export interface MySitesState {
  manageSites: {
    meta: {
      loadStatus: RequestStatus;
    };
    data: {
      totalSites: number;
      siteList: MySitesSiteList;
    };
  };
  createSite: {
    meta: {
      domainValidationStatus: RequestStatus;
      uploadLogoStatus: RequestStatus;
      convertLogoStatus: RequestStatus;
      generateLogoStatus: RequestStatus;
      updateJoinTypeStatus: RequestStatus;
      siteCreationStatus: RequestStatus;
    };
    data: {
      domain: string;
      subDomain: string;
      subDomainEnding: SubdomainEnding;
      domainType: WizardAction.DOMAIN | WizardAction.SUBDOMAIN;
      payProgram: PayProgramType;
      joinType: JoinType;
      logoUrl: string;
      logoData: string;
      validatedDomain: string;
      newReviewSiteId: number;
    };
  };
  promoteSite: {
    meta: {
      loadStatus: RequestStatus;
      createStatus: RequestStatus;
    };
    data: {
      promotedPrograms: { [id: number]: string[] };
      selectedSiteId: number;
      selectedProgramId: number;
    };
  };
  postback: {
    meta: {
      getPostbacksStatus: RequestStatus;
      getSitesStatus: RequestStatus;
      addPostbackStatus: RequestStatus;
      editPostbackStatus: RequestStatus;
      deletePostbackStatus: RequestStatus;
    };
    data: {
      postbacks: PostbackDetails[];
      sites: PostbackSiteListElement[];
      promotions: PostbackSiteListElement[];
      totalPages: number;
      firstLoad: boolean;
      pagination: PaginationSettings;
    };
  };
  createBlackLabel: {
    meta: {
      saveStatus: RequestStatus;
    };
  };
  timezones: {
    meta: {
      getStatus: RequestStatus;
    };
    data: {
      result: IDropDownOption[];
    };
  };
}

export interface PayoutProgramEnum {
  id: number;
  value: string;
}

export interface SkinReviewStatusEnum {
  name: string;
  value: number;
}

export interface SitePromotionEnum {
  name: string;
  siteId: number;
}

export interface SitePromotionStatusEnum {
  name: string;
  value: number;
}

export interface SiteListDetails {
  name: string;
  url: string;
  siteId: number;
  note: string;
  isSkinEditable: boolean;
  skinReviewId: number;
  skinReviewNewSite: boolean;
  skinReviewStatus: SkinReviewStatusEnum;
  payoutProgram: PayoutProgramEnum;
  isFreemium?: boolean;
}

export interface PostbackSiteListElement {
  name: string;
  siteId: number;
  id?: number;
  payoutProgram: PayoutProgramEnum;
}

export interface SitePromotionListDetails {
  id: number;
  url: string;
  note: string;
  name: SitePromotionEnum;
  status: SitePromotionStatusEnum;
  payoutProgram: PayoutProgramEnum;
}

// SITE LIST
export type GetSiteListResponse = WebserviceResponse<SiteListDetails[]>;

export type GetSitePromotionListResponse = WebserviceResponse<SitePromotionListDetails[]>;

export enum MySitesSiteType {
  whiteLabel = "WL",
  blackLabel = "BL",
  sitePromotion = "SM",
}

export interface MySitesSiteListRow {
  domain: string;
  url?: string;
  note?: string;
  payoutProgramId?: number;
  payoutProgramName?: string;
  statusId: number;
  statusName: string;
  isEditable: boolean;
  type: MySitesSiteType;
  setIsOpen?: (isOpen: boolean) => void;
  siteId?: number;
  reviewId?: number;
  newSite?: boolean;
}

export type MySitesSiteList = MySitesSiteListRow[];

export interface GetMyPromotionSitesResponse {
  results: string;
  promotions: WhiteLabelPromotionSite[];
}

export interface WhiteLabelPromotionSite {
  ABtest: boolean;
  editable: boolean;
  name: string;
  note: string;
  ownership: boolean;
  programname: string;
  referrerid: number;
  siteid: number;
  status: SiteReviewStatus;
  today: number;
  url: string;
  yesterday: number;
}

export interface MySitesDataType {
  siteId: number;
  editable: boolean;
  name: string;
  programName: string;
  status: SiteReviewStatus;
  newSite: boolean;
  skinReviewId: number | null;
  referrerId?: number | null;
  comment?: string;
  url?: string;
}

// CREATE NEW SITE
export interface PostValidateWhitelabelDomainPayload {
  domain: string;
}

export interface PostValidateWhitelabelDomainResponse {
  status: string;
  data: {
    available: boolean;
  };
}

export interface PostCreateSiteDraftPayload {
  url: string;
  payoutType: number;
  template: string;
}

export interface PostCreateSiteDraftResponse {
  status: string;
  data: {
    id: number;
  };
  error?: string;
}

export interface PostUpdateJoinTypePayload {
  jointype: number;
  new_review_site_id: number;
}

export interface PostUpdateJoinTypeResponse {
  success: true;
}

// PROMOTE SITE
export interface GetPromotionSitesResponse {
  result: string;
  promotions: Array<PromotionSite>;
}

export interface PromotionSite {
  domain: string;
  programname: string;
  referrerid: number;
  siteid: number;
  status: number;
  type: number;
}

export interface PostCreatePromotionPayload {
  programtype: number;
  siteid: number;
}

export interface PostCreatePromotionResponse {
  error?: string;
  success: boolean;
  details: {
    payoutprogramid: number;
    referrerid: number;
    sitepromotionid: number;
  };
}

// POSTBACKS
export interface GetPostbackListRequestBody extends PaginatedRequest {
  resellerId: number;
}

export interface PostbackDetails {
  id: number;
  resellerId: number;
  siteId: number;
  postbackUrl: string;
  createdAt: string;
  updatedAt: string | null;
  siteName: string;
  siteDomain: string;
  eventType: PostbackEventType;
  methodType: PostbackMethodType;
  type: PostbackSiteType;
}

export type GetPostbackListResponse = WebservicePaginatedResponse<PostbackDetails[]>;

export interface PostAddPostbackRequestBody {
  resellerId: number;
  siteIds: number[];
  sitePromotionIds: number[];
  postbackUrl: string;
  eventType: PostbackEventType;
  methodType: PostbackMethodType;
}

export type PostAddPostbackResponse = WebserviceResponse<[]>;

export interface PostEditPostbackRequestBody {
  postbackId: number;
  postbackUrl: string;
  methodType: PostbackMethodType;
}

export type PostEditPostbackResponse = WebserviceResponse<PostbackDetails>;

export interface PostDeletePostbackRequestBody {
  postbackId: number;
}

export type PostDeletePostbackResponse = WebserviceResponse<boolean>;

export interface PostValidatePostbackRequestBody {
  siteUrl: string;
  postbackUrl: string;
  eventType: PostbackEventType;
  methodType: PostbackMethodType;
}

export type PostValidatePostbackResponse = WebserviceResponse<boolean>;

// BLACK LABEL
export interface PostCreateBlackLabelSiteRequestBody {
  selectedProduct: string;
  resellerId: number;
  siteId: string;
  domain: string;
  description: string;
  useOwnBilling: boolean;
  preferredCommunicationChannel: string;
  contactInfo: string;
  emergencyContactInfo: string;
  timezone: string;
  language: string;
  forwardingTraffic: string;
}

export interface BlackLabelWebserviceResponse<T> {
  status: string;
  message?: string;
  errors?: { error: string; message: string }[];
  params?: { [key: string]: string[] };
  result?: T;
}

export type PostCreateBlackLabelSiteResponse = BlackLabelWebserviceResponse<boolean>;

export type GetTimezonesResponse = WebserviceResponse<IDropDownOption[]>;

export enum BlackLabelSiteReviewSelectedProduct {
  PRO = "pro",
  BLOCKS = "blocks",
  LITE = "lite",
  API = "api",
}

export enum BlackLabelSiteReviewPreferredCommunicationChannel {
  EMAIL = "email",
  SKYPE = "skype",
  PHONE = "phone",
}

export enum BlackLabelSiteReviewForwardingTrafficChannel {
  WHITELABEL = "whitelabel",
  STREAMATE = "streamate",
}

export interface BlackLabelSiteReviewDetails {
  id: number;
  siteId?: number;
  resellerId: number;
  status: BlackLabelSiteReviewStatusName;
  domain: string;
  selectedProduct?: BlackLabelSiteReviewSelectedProduct;
  language: string;
  preferredCommunicationChannel: BlackLabelSiteReviewPreferredCommunicationChannel;
  useOwnBilling: boolean;
  forwardingTraffic?: BlackLabelSiteReviewForwardingTrafficChannel;
  timezone?: string;
  created: string;
  updated: string;
}

export type GetListBlackLabelSiteReviewResponse = BlackLabelWebserviceResponse<
  PaginatedResponse<BlackLabelSiteReviewDetails[]>
>;

export type PostbackRequestAdditionalParam = {
  type: string;
  name: string;
};
